import { useEffect } from 'react';
import { useUIStore } from '@/store';

/**
 * Toast Notification Component
 *
 * Displays toast notifications from the UI store
 */
export const ToastContainer = () => {
  const { toasts, removeToast } = useUIStore();

  return (
    <div
      className="fixed top-4 right-4 space-y-2"
      // Must sit above .wl-overlay (z-index: 80), or any toast raised while a
      // modal is open renders behind the backdrop and is never seen.
      style={{ zIndex: 100 }}
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

interface ToastProps {
  toast: {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
    timestamp: number;
  };
  onClose: () => void;
}

const Toast = ({ toast, onClose }: ToastProps) => {
  useEffect(() => {
    const duration = toast.duration || 5000; // Default 5 seconds
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.duration, onClose]);

  const typeStyles = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-500 text-white',
  };

  const typeIcons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`${typeStyles[toast.type]} px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-md animate-slide-in`}
    >
      <span className="text-xl font-bold" aria-hidden="true">
        {typeIcons[toast.type]}
      </span>
      <p className="flex-1">{toast.message}</p>
      <button
        onClick={onClose}
        aria-label="Dismiss notification"
        className="text-white hover:text-gray-200 transition-colors"
      >
        ✕
      </button>
    </div>
  );
};
